import { useMemo } from "react";
import { SxProps, Theme } from "@mui/material";
import React from "react";

// ── Individual effect imports ──────────────────────────────────────────────────
import LightSweep from "./effects/LightSweep";
import BokehDust from "./effects/BokehDust";
import GlowBreathe from "./effects/GlowBreathe";
import TintShift from "./effects/TintShift";
import GodRays from "./effects/GodRays";
import Grain from "./effects/Grain";
import Droplets from "./effects/Droplets";
import Rain from "./effects/Rain";
import SteamFog from "./effects/SteamFog";
import Snow from "./effects/Snow";
import Leaves from "./effects/Leaves";
import Frost from "./effects/Frost";
import LensFlare from "./effects/LensFlare";
import SparkFlicker from "./effects/SparkFlicker";
import LightOn from "./effects/LightOn";
import ColorSweep from "./effects/ColorSweep";
import SparkleShine from "./effects/SparkleShine";
import Bubbles from "./effects/Bubbles";
import Sawdust from "./effects/Sawdust";
import PanelGlint from "./effects/PanelGlint";
import Confetti from "./effects/Confetti";
import TwinkleLights from "./effects/TwinkleLights";
import BalloonFloat from "./effects/BalloonFloat";
import Sprite from "./effects/Sprite";
import Parallax from "./effects/Parallax";
import TiltParallax from "./effects/TiltParallax";

/**
 * The two things a banner effect can produce:
 *
 *  imgSx   — sx props to spread onto the <Box component="img"> in the carousel.
 *            Used by kenburns-zoom, which must transform the actual photograph.
 *            null when the effect doesn't need to touch the image element.
 *
 *  overlay — an absolutely-positioned JSX element rendered as a sibling on top
 *            of the image (pointer-events:none). Used by light-sweep, droplets,
 *            and any future particle/overlay effects.
 *            null when the effect is implemented via imgSx only (kenburns) or
 *            when effect is 'none'.
 *
 * Why a hook instead of a component?
 *   kenburns-zoom must animate the real <img> element — scaling an empty
 *   transparent sibling div does nothing visible. Rather than letting
 *   BannerEffect return a "magic" prop-patching object that the caller has
 *   to know to apply, we surface both outputs explicitly so the carousel
 *   can spread imgSx onto its <Box component="img"> and place overlay as
 *   a sibling — each in the right place in the DOM.
 */
export interface BannerEffectResult {
  imgSx: SxProps<Theme> | null;
  overlay: React.ReactElement | null;
}

// ─────────────────────────────────────────────────────────────────────────────

export type EffectName =
  | "none"
  | "kenburns-zoom"
  | "kenburns-pan"
  | "light-sweep"
  | "bokeh-dust"
  | "glow-breathe"
  | "tint-shift"
  | "god-rays"
  | "grain"
  | "droplets"
  | "rain"
  | "steam-fog"
  | "snow"
  | "leaves"
  | "frost"
  | "lens-flare"
  | "spark-flicker"
  | "light-on"
  | "color-sweep"
  | "sparkle-shine"
  | "bubbles"
  | "sawdust"
  | "panel-glint"
  | "confetti"
  | "twinkle-lights"
  | "balloon-float"
  | "sprite"
  | "parallax"
  | "tilt-parallax";

/**
 * useBannerEffect
 *
 * Returns { imgSx, overlay } for the current banner's effect + config.
 *
 * Usage in the carousel:
 *
 *   const { imgSx, overlay } = useBannerEffect(banner.effect, banner.effectConfig);
 *
 *   <Box component="img" sx={{ ...baseImgSx, ...(imgSx ?? {}) }} />
 *   {overlay}
 */
export function useBannerEffect(
  effect: string,
  config?: Record<string, any> | null
): BannerEffectResult {
  const cfg = config ?? {};

  return useMemo<BannerEffectResult>(() => {
    switch (effect) {
      // ── kenburns — apply animation directly on the <img> via imgSx ──────────
      // This keeps the carousel structure untouched: image is always a plain
      // sibling, overlay is always rendered on top. No wrapper, no children.

      case "kenburns-zoom": {
        const scale     = cfg.scale     ?? 1.12;
        const dur       = cfg.dur       ?? 9;
        const direction = cfg.direction ?? "in";
        return {
          overlay: null,
          imgSx: {
            "@keyframes oot-kb-zoom": {
              from: { transform: `scale(${direction === "in" ? 1 : scale})` },
              to:   { transform: `scale(${direction === "in" ? scale : 1})` },
            },
            animation: `oot-kb-zoom ${dur}s ease-in-out infinite alternate`,
            transformOrigin: "center center",
            willChange: "transform",
          } as any,
        };
      }

      case "kenburns-pan": {
        const dur   = cfg.dur   ?? 10;
        const fromX = cfg.fromX ?? "0%";
        const toX   = cfg.toX   ?? "-6%";
        return {
          overlay: null,
          imgSx: {
            "@keyframes oot-kb-pan": {
              from: { transform: `translateX(${fromX}) scale(1.08)` },
              to:   { transform: `translateX(${toX}) scale(1.08)` },
            },
            animation: `oot-kb-pan ${dur}s ease-in-out infinite alternate`,
            willChange: "transform",
          } as any,
        };
      }

      // ── Overlay effects ──────────────────────────────────────────────────────

      case "light-sweep":
        return {
          imgSx: null,
          overlay: (
            <LightSweep
              key="light-sweep"
              duration={cfg.duration}
              delay={cfg.delay}
              angle={cfg.angle}
              color={cfg.color}
            />
          ),
        };

      case "bokeh-dust":
        return {
          imgSx: null,
          overlay: (
            <BokehDust
              key="bokeh-dust"
              count={cfg.count}
              color={cfg.color}
              minSize={cfg.minSize}
              maxSize={cfg.maxSize}
              dur={cfg.dur}
            />
          ),
        };

      case "glow-breathe":
        return {
          imgSx: null,
          overlay: <GlowBreathe key="glow-breathe" color={cfg.color} dur={cfg.dur} />,
        };

      case "tint-shift":
        return {
          imgSx: null,
          overlay: <TintShift key="tint-shift" colors={cfg.colors} dur={cfg.dur} />,
        };

      case "god-rays":
        return {
          imgSx: null,
          overlay: (
            <GodRays key="god-rays" color={cfg.color} angle={cfg.angle} dur={cfg.dur} />
          ),
        };

      case "grain":
        return {
          imgSx: null,
          overlay: <Grain key="grain" opacity={cfg.opacity} />,
        };

      case "droplets":
        return {
          imgSx: null,
          overlay: (
            <Droplets
              key="droplets"
              count={cfg.count}
              color={cfg.color}
              minDur={cfg.minDur}
              maxDur={cfg.maxDur}
              minSize={cfg.minSize}
              maxSize={cfg.maxSize}
            />
          ),
        };

      case "rain":
        return {
          imgSx: null,
          overlay: (
            <Rain
              key="rain"
              count={cfg.count}
              color={cfg.color}
              minDur={cfg.minDur}
              maxDur={cfg.maxDur}
              angle={cfg.angle}
            />
          ),
        };

      case "steam-fog":
        return {
          imgSx: null,
          overlay: (
            <SteamFog key="steam-fog" count={cfg.count} color={cfg.color} dur={cfg.dur} />
          ),
        };

      case "snow":
        return {
          imgSx: null,
          overlay: (
            <Snow
              key="snow"
              count={cfg.count}
              color={cfg.color}
              minSize={cfg.minSize}
              maxSize={cfg.maxSize}
              minDur={cfg.minDur}
              maxDur={cfg.maxDur}
            />
          ),
        };

      case "leaves":
        return {
          imgSx: null,
          overlay: (
            <Leaves key="leaves" count={cfg.count} color={cfg.color} dur={cfg.dur} />
          ),
        };

      case "frost":
        return {
          imgSx: null,
          overlay: (
            <Frost
              key="frost"
              intensity={cfg.intensity}
              color={cfg.color}
              dur={cfg.dur}
            />
          ),
        };

      case "lens-flare":
        return {
          imgSx: null,
          overlay: <LensFlare key="lens-flare" color={cfg.color} dur={cfg.dur} />,
        };

      case "spark-flicker":
        return {
          imgSx: null,
          overlay: <SparkFlicker key="spark-flicker" count={cfg.count} color={cfg.color} />,
        };

      case "light-on":
        return {
          imgSx: null,
          overlay: <LightOn key="light-on" color={cfg.color} dur={cfg.dur} />,
        };

      case "color-sweep":
        return {
          imgSx: null,
          overlay: (
            <ColorSweep
              key="color-sweep"
              color={cfg.color}
              dur={cfg.dur}
              direction={cfg.direction}
            />
          ),
        };

      case "sparkle-shine":
        return {
          imgSx: null,
          overlay: (
            <SparkleShine key="sparkle-shine" count={cfg.count} color={cfg.color} />
          ),
        };

      case "bubbles":
        return {
          imgSx: null,
          overlay: (
            <Bubbles
              key="bubbles"
              count={cfg.count}
              color={cfg.color}
              minSize={cfg.minSize}
              maxSize={cfg.maxSize}
              dur={cfg.dur}
            />
          ),
        };

      case "sawdust":
        return {
          imgSx: null,
          overlay: <Sawdust key="sawdust" count={cfg.count} color={cfg.color} dur={cfg.dur} />,
        };

      case "panel-glint":
        return {
          imgSx: null,
          overlay: <PanelGlint key="panel-glint" color={cfg.color} dur={cfg.dur} />,
        };

      case "confetti":
        return {
          imgSx: null,
          overlay: (
            <Confetti key="confetti" count={cfg.count} colors={cfg.colors} dur={cfg.dur} />
          ),
        };

      case "twinkle-lights":
        return {
          imgSx: null,
          overlay: (
            <TwinkleLights
              key="twinkle-lights"
              count={cfg.count}
              colors={cfg.colors}
              edge={cfg.edge}
            />
          ),
        };

      case "balloon-float":
        return {
          imgSx: null,
          overlay: (
            <BalloonFloat
              key="balloon-float"
              count={cfg.count}
              colors={cfg.colors}
              dur={cfg.dur}
            />
          ),
        };

      case "sprite":
        return {
          imgSx: null,
          overlay: (
            <Sprite
              key="sprite"
              spriteUrl={cfg.spriteUrl}
              from={cfg.from}
              to={cfg.to}
              scale={cfg.scale}
              dur={cfg.dur}
              top={cfg.top}
              width={cfg.width}
            />
          ),
        };

      case "parallax":
        return {
          imgSx: null,
          overlay: (
            <Parallax key="parallax" layers={cfg.layers ?? []} dur={cfg.dur} />
          ),
        };

      case "tilt-parallax":
        return {
          imgSx: null,
          overlay: <TiltParallax key="tilt-parallax" layers={cfg.layers ?? []} />,
        };

      case "none":
      default:
        return { imgSx: null, overlay: null };
    }
    // cfg is a plain object from JSON — stringify gives a stable dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effect, JSON.stringify(cfg)]);
}
